import React from 'react'
import { Card as CommonCard, CardContent as MuiCardContent, IconButton } from '@mui/material'
import { Star, Lightbulb } from 'lucide-react'
import { ShareNetwork } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

interface PracticalAdvice {
  title: string
  description?: string
}

interface DirectAnswerCardProps {
  directAnswer?: string
  finalConclusion?: string
  practicalAdvice?: PracticalAdvice[]
  onShare?: () => void
  showShareButton?: boolean
}

export default function DirectAnswerCard({
  directAnswer,
  finalConclusion,
  practicalAdvice = [],
  onShare,
  showShareButton = false
}: DirectAnswerCardProps) {
  const { t } = useTranslation()

  // Use direct answer if available, otherwise use first sentence of final conclusion
  const displayAnswer = directAnswer || finalConclusion?.split('.')[0]

  return (
    <CommonCard sx={{ mb: 6, mt: 1.2, bgcolor: 'rgba(255, 255, 255, 0.7)', }}>
      <MuiCardContent>
        {/* Direct Answer Section */}
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-[#2B2E4A] flex-shrink-0" />
          <h3
            id="direct-answer-heading"
            className="text-lg font-['Playfair_Display'] text-[#2B2E4A] font-semibold mb-0 mt-0 m-0"
          >
            {t('readingPage.directAnswer')}
          </h3>
        </div>
        <div className="flex flex-col gap-4">
          <p className="text-md leading-relaxed break-words text-[#2B2E4A]/70 font-['Plus_Jakarta_Sans'] mb-0">
            {displayAnswer}
          </p>
        </div>

        {/* Practical Advice Section */}
        {practicalAdvice && practicalAdvice.length > 0 && (
          <>
            <div className="flex items-center gap-2 mt-2">
              <Lightbulb className="h-4 w-4 text-[#2B2E4A] flex-shrink-0" />
              <h3
                id="interactions-heading"
                className="text-lg font-['Playfair_Display'] text-[#2B2E4A] font-semibold mb-0 mt-0 m-0"
              >
                {t('readingPage.practicalAdvice')}
              </h3>
            </div>
            <div className="flex flex-col gap-2 mt-2">
              {practicalAdvice.map((advice: PracticalAdvice, index: number) => (
                <div key={index} className="flex items-start gap-2">
                  <span className="text-[#2B2E4A]/70 font-['Plus_Jakarta_Sans'] min-w-[20px]">
                    {index + 1}.
                  </span>
                  <p className="text-md leading-relaxed break-words text-[#2B2E4A]/70 font-['Plus_Jakarta_Sans'] m-0">
                    {advice.title}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Share Button */}
        {showShareButton && onShare && (
          <div className="flex justify-end mt-0">
            <IconButton
              onClick={onShare}
              size="small"
              sx={{
                color: '#2B2E4A',
                display: 'none',
                bgcolor: 'rgba(43, 46, 74, 0.05)',
                '&:hover': {
                  bgcolor: 'rgba(43, 46, 74, 0.1)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 200ms',
              }}
            >
              <ShareNetwork size={20} />
            </IconButton>
          </div>
        )}
      </MuiCardContent>
    </CommonCard>
  )
}
